import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  isAllowedAttachment,
  MAX_UPLOAD_BYTES,
} from "@touchworks/shared/support/attachmentTypes.js";
import { formatBytes } from "@/utils/cmsUpload";

/**
 * Pending attachments for a Lucas message.
 *
 * Validation runs against the *same* allowlist the API enforces
 * (`@touchworks/shared/support`). Two copies of that list drift, and when they
 * do the failure is silent in the worst direction: the file is accepted on
 * screen and rejected on submit, after the person already typed the question.
 *
 * Upload starts the moment a file is attached rather than on submit, so the URL
 * already exists by the time the question is written — the send, and the
 * transition into the chat, never wait on the network.
 *
 * Thumbnails come from `createObjectURL`, not from the uploaded URL: waiting
 * for S3 would leave the tray blank for the whole upload.
 */

let sequence = 0;

export function useLucasAttachments({ upload }) {
  const [attachments, setAttachments] = useState([]);
  // Object URLs outlive the component unless revoked; keep a live handle so
  // unmount can release whatever is still pending.
  const previewUrls = useRef(new Set());

  useEffect(
    () => () => {
      for (const url of previewUrls.current) URL.revokeObjectURL(url);
      previewUrls.current.clear();
    },
    [],
  );

  const reject = useCallback((file) => {
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(
        `${file.name} excede o limite de ${formatBytes(MAX_UPLOAD_BYTES)}`,
      );
      return true;
    }
    if (!isAllowedAttachment({ filename: file.name, mimeType: file.type })) {
      toast.error(`${file.name}: tipo de arquivo não suportado`);
      return true;
    }
    return false;
  }, []);

  const startUpload = useCallback(
    async (id, file) => {
      try {
        const url = await upload(file);
        setAttachments((prev) =>
          prev.map((a) => (a.id === id ? { ...a, url, status: "ready" } : a)),
        );
      } catch {
        // Keep the row, marked failed. Removing it silently would let the
        // person believe the file went along with the question.
        setAttachments((prev) =>
          prev.map((a) => (a.id === id ? { ...a, status: "error" } : a)),
        );
      }
    },
    [upload],
  );

  const add = useCallback(
    (files) => {
      const accepted = [...(files || [])].filter((file) => !reject(file));
      if (!accepted.length) return;

      const entries = accepted.map((file) => {
        const isImage = (file.type || "").startsWith("image/");
        const previewUrl = isImage ? URL.createObjectURL(file) : null;
        if (previewUrl) previewUrls.current.add(previewUrl);

        return {
          id: `att-${++sequence}`,
          name: file.name,
          mimeType: file.type,
          size: file.size,
          isImage,
          previewUrl,
          url: null,
          status: "uploading",
        };
      });

      setAttachments((prev) => [...prev, ...entries]);
      entries.forEach((entry, index) => startUpload(entry.id, accepted[index]));
    },
    [reject, startUpload],
  );

  /** Ctrl+V — only the file parts; a plain text paste must stay a text paste. */
  const addFromClipboard = useCallback(
    (clipboardData) => {
      const files = [...(clipboardData?.files || [])];
      if (files.length) {
        add(files);
        return true;
      }
      const fromItems = [...(clipboardData?.items || [])]
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile())
        .filter(Boolean);
      if (fromItems.length) {
        add(fromItems);
        return true;
      }
      return false;
    },
    [add],
  );

  const remove = useCallback((id) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.id === id);
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
        previewUrls.current.delete(target.previewUrl);
      }
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  const clear = useCallback(() => {
    setAttachments((prev) => {
      for (const a of prev) {
        if (a.previewUrl) {
          URL.revokeObjectURL(a.previewUrl);
          previewUrls.current.delete(a.previewUrl);
        }
      }
      return [];
    });
  }, []);

  return {
    attachments,
    readyAttachments: attachments.filter((a) => a.status === "ready"),
    isUploading: attachments.some((a) => a.status === "uploading"),
    add,
    addFromClipboard,
    remove,
    clear,
  };
}

export default useLucasAttachments;
